import { useEffect, useRef, useCallback } from 'react';

import type { NotificationDto } from '@/types/notification';

// ============================================
// CONSTANTS
// ============================================

const CHANNEL_NAME = 'wp-notifications';
const SSE_URL = '/api/notification-service/v1/notifications/stream';
const HEARTBEAT_INTERVAL_MS = 3_000;
const HEARTBEAT_TIMEOUT_MS = 5_000;
const LEADER_CLAIM_WAIT_MS = 500;

// ============================================
// Message Types
// ============================================

interface LeaderClaimMessage {
  type: 'leader-claim';
  tabId: string;
}

interface LeaderAckMessage {
  type: 'leader-ack';
  tabId: string;
}

interface HeartbeatMessage {
  type: 'heartbeat';
  tabId: string;
}

interface NotificationMessage {
  type: 'notification';
  payload: NotificationDto;
}

// New leader announces takeover — old leader must step down
interface LeaderTakeoverMessage {
  type: 'leader-takeover';
  tabId: string;
}

type ChannelMessage =
  | LeaderClaimMessage
  | LeaderAckMessage
  | HeartbeatMessage
  | NotificationMessage
  | LeaderTakeoverMessage;

// ============================================
// Hook
// ============================================

interface UseNotificationStreamOptions {
  onNotification: (notification: NotificationDto) => void;
  enabled?: boolean;
}

export function useNotificationStream({
  onNotification,
  enabled = true,
}: UseNotificationStreamOptions): void {
  const onNotificationRef = useRef(onNotification);
  onNotificationRef.current = onNotification;

  const eventSourceRef = useRef<EventSource | null>(null);
  const channelRef = useRef<BroadcastChannel | null>(null);
  const heartbeatIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const heartbeatCheckIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const leaderClaimTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isLeaderRef = useRef(false);
  const lastHeartbeatRef = useRef(Date.now());
  const tabIdRef = useRef(crypto.randomUUID());

  // ── Cleanup helpers ──

  const closeEventSource = useCallback(() => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
  }, []);

  const clearHeartbeatInterval = useCallback(() => {
    if (heartbeatIntervalRef.current) {
      clearInterval(heartbeatIntervalRef.current);
      heartbeatIntervalRef.current = null;
    }
  }, []);

  const clearHeartbeatCheck = useCallback(() => {
    if (heartbeatCheckIntervalRef.current) {
      clearInterval(heartbeatCheckIntervalRef.current);
      heartbeatCheckIntervalRef.current = null;
    }
  }, []);

  const clearLeaderClaimTimer = useCallback(() => {
    if (leaderClaimTimerRef.current) {
      clearTimeout(leaderClaimTimerRef.current);
      leaderClaimTimerRef.current = null;
    }
  }, []);

  // ── Step down: relinquish leadership, become follower ──

  const stepDown = useCallback(() => {
    isLeaderRef.current = false;
    closeEventSource();
    clearHeartbeatInterval();

    // Start monitoring the new leader's heartbeats
    lastHeartbeatRef.current = Date.now();
    clearHeartbeatCheck();
    heartbeatCheckIntervalRef.current = setInterval(() => {
      if (Date.now() - lastHeartbeatRef.current > HEARTBEAT_TIMEOUT_MS) {
        // New leader also went away — re-promote self
        clearHeartbeatCheck();
        startLeaderInternal();
      }
    }, 1_000);
  }, [closeEventSource, clearHeartbeatInterval, clearHeartbeatCheck]);

  // ── Leader behavior ──

  const startLeaderInternal = useCallback(() => {
    // Close any existing connection first (idempotent)
    closeEventSource();
    clearHeartbeatInterval();
    isLeaderRef.current = true;

    // Announce takeover — any other leader must step down
    channelRef.current?.postMessage({
      type: 'leader-takeover',
      tabId: tabIdRef.current,
    } satisfies LeaderTakeoverMessage);

    // Open SSE connection
    const es = new EventSource(SSE_URL);
    eventSourceRef.current = es;

    es.onmessage = (event: MessageEvent<string>) => {
      try {
        const notification = JSON.parse(event.data) as NotificationDto;

        // Broadcast to other tabs
        channelRef.current?.postMessage({
          type: 'notification',
          payload: notification,
        } satisfies NotificationMessage);

        // Handle locally
        onNotificationRef.current(notification);
      } catch {
        // Ignore malformed SSE data
      }
    };

    es.onerror = () => {
      // EventSource auto-reconnects — no action needed
    };

    // Start heartbeat broadcast
    heartbeatIntervalRef.current = setInterval(() => {
      channelRef.current?.postMessage({
        type: 'heartbeat',
        tabId: tabIdRef.current,
      } satisfies HeartbeatMessage);
    }, HEARTBEAT_INTERVAL_MS);

    // Stop checking for heartbeats (we are the leader)
    clearHeartbeatCheck();
  }, [closeEventSource, clearHeartbeatInterval, clearHeartbeatCheck]);

  // ── Follower behavior ──

  const startFollower = useCallback(() => {
    isLeaderRef.current = false;
    closeEventSource();
    clearHeartbeatInterval();

    // Monitor leader heartbeats
    lastHeartbeatRef.current = Date.now();
    clearHeartbeatCheck();
    heartbeatCheckIntervalRef.current = setInterval(() => {
      if (Date.now() - lastHeartbeatRef.current > HEARTBEAT_TIMEOUT_MS) {
        // Leader went away — promote self
        clearHeartbeatCheck();
        startLeaderInternal();
      }
    }, 1_000);
  }, [closeEventSource, clearHeartbeatInterval, clearHeartbeatCheck, startLeaderInternal]);

  // ── Main effect ──

  useEffect(() => {
    if (!enabled) return;

    // BroadcastChannel not supported — fall back to always-leader
    if (typeof BroadcastChannel === 'undefined') {
      startLeaderInternal();
      return () => {
        closeEventSource();
        clearHeartbeatInterval();
      };
    }

    const channel = new BroadcastChannel(CHANNEL_NAME);
    channelRef.current = channel;

    let claimedLeadership = false;

    channel.onmessage = (event: MessageEvent<ChannelMessage>) => {
      const msg = event.data;

      switch (msg.type) {
        case 'leader-claim':
          // Another tab is claiming leadership — if we are leader, ack to block them
          if (isLeaderRef.current) {
            channel.postMessage({
              type: 'leader-ack',
              tabId: tabIdRef.current,
            } satisfies LeaderAckMessage);
          }
          break;

        case 'leader-ack':
          // A leader already exists — become follower
          claimedLeadership = false;
          clearLeaderClaimTimer();
          if (!isLeaderRef.current) {
            startFollower();
          }
          break;

        case 'leader-takeover':
          // Another tab has taken leadership — if we are leader, step down
          if (isLeaderRef.current && msg.tabId !== tabIdRef.current) {
            stepDown();
          }
          break;

        case 'heartbeat':
          lastHeartbeatRef.current = Date.now();
          break;

        case 'notification':
          // Only followers process broadcast notifications (leader handled it in onmessage)
          if (!isLeaderRef.current) {
            onNotificationRef.current(msg.payload);
          }
          break;
      }
    };

    // Attempt leader election
    claimedLeadership = true;
    channel.postMessage({
      type: 'leader-claim',
      tabId: tabIdRef.current,
    } satisfies LeaderClaimMessage);

    // Wait for potential ack — if none arrives, become leader
    leaderClaimTimerRef.current = setTimeout(() => {
      if (claimedLeadership) {
        startLeaderInternal();
      }
    }, LEADER_CLAIM_WAIT_MS);

    return () => {
      clearLeaderClaimTimer();
      closeEventSource();
      clearHeartbeatInterval();
      clearHeartbeatCheck();
      channelRef.current?.close();
      channelRef.current = null;
      isLeaderRef.current = false;
    };
  }, [
    enabled,
    startLeaderInternal,
    startFollower,
    stepDown,
    closeEventSource,
    clearHeartbeatInterval,
    clearHeartbeatCheck,
    clearLeaderClaimTimer,
  ]);
}
